<template>
  <div>
    <app-header title="">
      <template v-slot:actions>
        <c-button @click="update" class="btn btn-primary mt-2 mt-xl-0">
          <i class="far fa-sync"></i> Run Xero Sync
        </c-button>
      </template>
    </app-header>

    <div class="container my-5">
      <div class="row mb-4">
        <div class="col d-flex align-items-center">
          <h2 class="mb-0">Financial Dashboard</h2>
        </div>
        <div class="col-12 col-md-4 col-xl-3">
          <multiselect
            v-model="financialTarget"
            :options="financialTargets"
            label="financial_year"
            track-by="id"
            placeholder="Select Year"
            :show-labels="false"
            :allow-empty="false"
            :searchable="false"
            @select="getData($event.id)"
          ></multiselect>
        </div>
      </div>

      <div v-if="loading" class="py-5 text-center">
        <span class="spinner-border text-primary"></span>
      </div>

      <template v-else>
        <div class="row">
          <div class="col-md-6 col-xl-4 mb-4">
            <div class="card card-widget h-100">
              <div class="card-body">
                <h3 class="widget-title">Revenue <span class="badge rounded-pill bg-primary float-end">Annual</span></h3>
                <div class="row">
                  <div class="col-6 mb-3">
                    <p class="small text-muted mb-1">Current YTD</p>
                    <p class="lead mb-0">${{ data.annual_revenue | currency0 }}</p>
                  </div>
                  <div class="col-6 mb-3">
                    <p class="small text-muted mb-1">Target</p>
                    <p class="lead mb-0">${{ data.annual_target | currency0 }}</p>
                  </div>
                  <div class="col-6">
                    <p class="small text-muted mb-1">Remaining</p>
                    <p class="lead mb-0">${{ data.annual_target - data.annual_revenue | currency0 }}</p>
                  </div>
                  <div class="col-6">
                    <p class="small text-muted mb-1">Position</p>
                    <p class="lead mb-0" :class="data.position >= 0 ? 'text-success' : 'text-danger'">
                      {{ data.position >= 0 ? '+' : '-' }} ${{ Math.abs(data.position) | currency0 }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-xl-4 mb-4">
            <div class="card card-widget h-100">
              <div class="card-body">
                <h3 class="widget-title">Revenue <span class="badge rounded-pill bg-info float-end">Month</span></h3>
                <div class="row">
                  <div class="col-6 mb-3">
                    <p class="small text-muted mb-1">Current</p>
                    <p class="lead mb-0">${{ data.current_month_revenue | currency0 }}</p>
                  </div>
                  <div class="col-6 mb-3">
                    <p class="small text-muted mb-1">Target</p>
                    <p class="lead mb-0">${{ data.monthly_target | currency0 }}</p>
                  </div>
                  <div class="col-6">
                    <p class="small text-muted mb-1">Remaining</p>
                    <p class="lead mb-0" :class="monthlyRemaining > 0 ? 'text-danger' : 'text-success'">
                      ${{ Math.max(monthlyRemaining, 0) | currency0 }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-xl-4 mb-4">
            <div class="card card-widget h-100">
              <div class="card-body">
                <h3 class="widget-title">License Fees <span class="badge rounded-pill bg-primary float-end">Annual</span></h3>
                <p class="small text-muted mb-1">Current YTD</p>
                <p class="lead mb-0">${{ data.license | currency0 }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-xl-6 mb-4">
            <div class="card h-100">
              <div class="card-body">
                <h3 class="card-title">Annual Revenue</h3>
                <columns-chart :columns="data.monthly_revenue" type="line" :targets="targets" />
              </div>
            </div>
          </div>

          <div class="col-xl-6 mb-4">
            <div class="card h-100">
              <div class="card-body">
                <h3 class="card-title">Invoicing</h3>
                <columns-chart
                  :columns="data.monthly_invoiced"
                  type="bar"
                  :stack-group="['Planned', 'Raised']"
                  :targets="monthlyTargets"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { notify, http } from 'utils'
import ColumnsChart from './charts/ColumnsChart'

export default {
  components: {
    Multiselect,
    ColumnsChart
  },

  data () {
    return {
      loading: true,
      data: {},
      financialTarget: null,
      financialTargets: []
    }
  },

  computed: {
    monthlyRemaining () {
      return this.data.monthly_target - this.data.current_month_revenue
    },

    targets () {
      return [
        { value: this.data.annual_target, label: 'Revenue Target', color: '#ff6b6b' },
        { value: this.data.annual_target_profit, label: 'Profit Target', color: '#2ba97a' }
      ].filter(({ value }) => value > 0)
    },

    monthlyTargets () {
      return [
        { value: this.data.monthly_target, label: 'Revenue Target', color: '#ff6b6b' },
        { value: this.data.monthly_target_profit, label: 'Profit Target', color: '#2ba97a' }
      ].filter(({ value }) => value > 0)
    }
  },

  created () {
    http.get('/financial-targets').then((response) => {
      this.financialTargets = response.data
    })

    this.getData()
  },

  methods: {
    getData (financialTargetId = '') {
      this.loading = true

      return http.get('/dashboards/financial/' + financialTargetId).then((response) => {
        this.data = response.data
        this.financialTarget = response.data.financial_target
      }).finally(() => {
        this.loading = false
      })
    },

    update () {
      return http.put('/dashboards/financial', {}).then(() => {
        notify.success('Sync Running, refresh in a minute to see the changes')
      }).catch(() => {
        notify.error('Failed to start the Xero sync, please try again.')
      })
    }
  }
}
</script>
